import { useEffect, useState } from "react";
import api from "@/lib/api";
import { compact, inr, CHANNEL_COLORS } from "@/lib/format";
import { StatCard, DaysToggle, Skeleton } from "@/components/Shared";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Overview() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/dashboard/overview?days=${days}`).then(({ data }) => setData(data));
  }, [days]);

  if (!data)
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-80" />
      </div>
    );

  const k = data.kpis;
  return (
    <div className="space-y-6" data-testid="overview-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Live Amazon.in data · last {days} days vs previous {days} days. Flipkart & Website join automatically once connected.</p>
        <DaysToggle value={days} onChange={setDays} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Gross Revenue" value={compact(k.revenue)} delta={k.revenue_delta} sub="vs prev period" testId="kpi-revenue" />
        <StatCard label="Orders" value={k.orders.toLocaleString("en-IN")} delta={k.orders_delta} sub="vs prev period" testId="kpi-orders" />
        <StatCard label="Units Sold" value={k.units.toLocaleString("en-IN")} delta={k.units_delta} sub="vs prev period" testId="kpi-units" />
        <StatCard label="Avg Order Value" value={inr(k.aov)} delta={k.aov_delta} sub="vs prev period" testId="kpi-aov" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Fees" value={compact(k.fees)} sub={`${((k.fees / k.revenue) * 100).toFixed(1)}% of revenue`} testId="kpi-fees" />
        <StatCard label="Net Revenue" value={compact(k.net_revenue)} sub="after all fees" testId="kpi-net" />
        <StatCard label="Refunds" value={compact(k.refunds)} sub="returned orders" testId="kpi-refunds" />
        <StatCard label="Return Rate" value={`${k.return_rate}%`} sub="of all orders" testId="kpi-return-rate" />
      </div>
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm" data-testid="revenue-trend-chart">
        <h3 className="font-heading text-base font-semibold tracking-tight mb-1">Daily revenue by channel</h3>
        <p className="text-xs text-muted-foreground mb-5">Stacked gross revenue (₹) per day</p>
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={data.trend}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} minTickGap={30} stroke="hsl(var(--muted-foreground))" />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => compact(v)} stroke="hsl(var(--muted-foreground))" width={70} />
            <Tooltip formatter={(v, n) => [inr(v), n]} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
            {(data.channels || ["Amazon"]).map((ch) => (
              <Area key={ch} type="monotone" dataKey={ch} stackId="1" stroke={CHANNEL_COLORS[ch]} fill={CHANNEL_COLORS[ch]} fillOpacity={0.55} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
        <div className="flex gap-6 mt-3">
          {(data.channels || ["Amazon"]).map((ch) => (
            <span key={ch} className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: CHANNEL_COLORS[ch] }} /> {ch}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
